import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Printer, GraduationCap, BookOpen, User, Users, ClipboardList } from 'lucide-react';
import { HomeworkItem, EmptyHomework } from '../../components/HomeworkItem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClassDetail = () => {
    const { classId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);

    useEffect(() => {
        axios.get(`${API_URL}/api/classes/${classId}/students`, { withCredentials: true })
            .then(res => setData(res.data))
            .catch(() => toast.error('Failed to load class'))
            .finally(() => setLoading(false));
    }, [classId]);

    const handlePrint = () => {
        const content = printRef.current;
        if (!content) return;
        // Escape any HTML special chars in the dynamic title
        const escapeHtml = (s) => String(s || 'Class').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
        const safeTitle = escapeHtml(data?.class?.arabic_name);
        // Use DOM manipulation instead of document.write() (which is unsafe and deprecated).
        const win = window.open('', '_blank');
        if (!win) return;
        // Set up the print window using DOM APIs — no document.write().
        win.document.title = safeTitle;
        const style = win.document.createElement('style');
        style.textContent = `
            body { font-family: Arial, sans-serif; padding: 30px; direction: ltr; }
            h1 { font-size: 22px; margin-bottom: 5px; }
            h2 { font-size: 16px; color: #555; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ccc; padding: 8px 12px; text-align: left; font-size: 13px; }
            th { background: #064E3B; color: white; }
            tr:nth-child(even) { background: #f9f9f9; }
            .total { margin-top: 15px; font-weight: bold; font-size: 14px; }
            .arabic { direction: rtl; text-align: right; }
            @media print { body { padding: 15px; } }
        `;
        win.document.head.appendChild(style);
        // Clone the React-rendered node (React auto-escapes text content).
        const clone = content.cloneNode(true);
        win.document.body.appendChild(clone);
        // Wait for the print window's document to fully load (fonts + layout) before triggering print.
        // Using onload ensures the cloned <table> has been laid out on slow renders.
        win.focus();
        if (win.document.readyState === 'complete') {
            win.print();
        } else {
            win.onload = () => win.print();
        }
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;
    if (!data) return <DashboardLayout><div className="text-center py-12"><p>Class not found</p></div></DashboardLayout>;

    const { class: cls, program, teacher, students, total } = data;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="class-detail-page">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{cls?.arabic_name || cls?.english_name || cls?.name}</h1>
                            <p className="text-sm text-[#4B5563]">Code: <span className="font-mono">{cls?.code}</span></p>
                        </div>
                    </div>
                    <Button onClick={handlePrint} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="print-class-btn"><Printer className="h-4 w-4 mr-2" />Print</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="card-surface"><CardContent className="p-4 flex items-center gap-3"><BookOpen className="h-8 w-8 text-[#D97706]" /><div><p className="text-xs text-[#4B5563]">Program</p><p className="font-medium">{program?.name || '—'}</p></div></CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4 flex items-center gap-3"><GraduationCap className="h-8 w-8 text-[#064E3B]" /><div><p className="text-xs text-[#4B5563]">Class</p><p className="font-medium">{cls?.arabic_name || cls?.english_name}</p><p className="text-xs">{cls?.english_name && cls?.arabic_name ? cls?.english_name : ''}</p></div></CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4 flex items-center gap-3"><User className="h-8 w-8 text-[#2563EB]" /><div><p className="text-xs text-[#4B5563]">Teacher</p><p className="font-medium">{teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Unassigned'}</p></div></CardContent></Card>
                </div>

                <div ref={printRef}>
                    <h1 style={{display:'none'}}>{program?.name} - {cls?.arabic_name || cls?.english_name} - Teacher: {teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Unassigned'}</h1>
                    <Card className="card-surface">
                        <CardHeader><CardTitle className="font-['Outfit']">Students ({total})</CardTitle></CardHeader>
                        <CardContent className="p-0">
                            {students.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Full Name</TableHead><TableHead>Arabic Name</TableHead><TableHead>Date of Birth</TableHead><TableHead>Gender</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {students.map((s, i) => (
                                            <TableRow key={s.id}>
                                                <TableCell className="tabular-nums font-medium">{i + 1}</TableCell>
                                                <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                                                <TableCell dir="rtl">{s.arabic_name}</TableCell>
                                                <TableCell className="tabular-nums">{s.date_of_birth || '—'}</TableCell>
                                                <TableCell className="capitalize">{s.gender}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="p-8 text-center text-[#4B5563]">No students in this class</div>}
                        </CardContent>
                    </Card>
                    <p className="total mt-3 text-sm font-semibold text-[#1F2937]">Total Students: {total}</p>
                </div>

                <ClassGroupsAndHomework classId={classId} />
            </div>
        </DashboardLayout>
    );
};

const ClassGroupsAndHomework = ({ classId }) => {
    const [groups, setGroups] = useState([]);
    const [hw, setHw] = useState([]);
    useEffect(() => {
        axios.get(`${API_URL}/api/homework/groups?class_id=${classId}`, { withCredentials: true }).then(r => setGroups(r.data || [])).catch(() => {});
        axios.get(`${API_URL}/api/homework?class_id=${classId}`, { withCredentials: true }).then(r => setHw((r.data || []).slice(0, 30))).catch(() => {});
    }, [classId]);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="card-surface" data-testid="class-groups-card">
                <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><Users className="h-5 w-5 text-[#5B21B6]" />Student groups ({groups.length})</CardTitle></CardHeader>
                <CardContent className="p-0 divide-y">
                    {groups.length === 0 && <p className="p-6 text-sm text-[#6B7280] text-center">No groups created by the teachers yet.</p>}
                    {groups.map(g => <div key={g.id} className="p-3" data-testid={`class-group-${g.id}`}><div className="font-medium">{g.name} <Badge variant="outline" className="ml-1">{g.students.length}</Badge> <span className="text-xs text-[#6B7280]">· {g.teacher_name}</span></div><p className="text-xs text-[#6B7280]">{g.students.map(s => s.name).join(', ') || 'No students'}</p></div>)}
                </CardContent>
            </Card>
            <Card className="card-surface" data-testid="class-homework-card">
                <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#064E3B]" />Recent homework</CardTitle></CardHeader>
                <CardContent className="p-0">{hw.length === 0 ? <EmptyHomework /> : <div className="divide-y max-h-[480px] overflow-auto">{hw.map(h => <HomeworkItem key={h.id} h={h} showTeacher />)}</div>}</CardContent>
            </Card>
        </div>
    );
};

export default ClassDetail;
